import React, { useState, useEffect } from "react";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";
import { useNavigate } from "react-router-dom";

export default function ManageLanguages() {
  const [token, setToken] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      const t = await getTokenInternal();
      if (!t || !["management", "owner", "helper"].includes(t.account.role)) {
        navigate("/");
        return;
      }
      setToken(t);
      await fetchLanguages(t);
    };
    init();
  }, [navigate]);

  const fetchLanguages = async (t = token) => {
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/languages/manage`, {
      headers: { Authorization: `Bearer ${t?.access_token}` },
    });
    if (res.ok) setLanguages(await res.json());
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage("");
    const trimmedCode = code.trim();
    const trimmedName = name.trim();
    if (!trimmedCode || !trimmedName) {
      setMessage("Both code and name are required.");
      return;
    }
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/languages/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token?.access_token}`,
      },
      body: JSON.stringify({ code: trimmedCode, name: trimmedName }),
    });
    if (res.ok) {
      setCode("");
      setName("");
      setMessage(`Language "${trimmedName}" added.`);
      await fetchLanguages();
    } else {
      setMessage("Failed to add language. It may already exist.");
    }
  };

  const startEdit = (lang) => {
    setEditingId(lang.id);
    setEditCode(lang.code);
    setEditName(lang.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditCode("");
    setEditName("");
  };

  const handleUpdate = async (id) => {
    const trimmedCode = editCode.trim();
    const trimmedName = editName.trim();
    if (!trimmedCode || !trimmedName) return;
    const res = await fetch(`${process.env.REACT_APP_API_HOST}/api/languages/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token?.access_token}`,
      },
      body: JSON.stringify({ code: trimmedCode, name: trimmedName }),
    });
    if (res.ok) {
      cancelEdit();
      await fetchLanguages();
    }
  };

  const handleDelete = async (id) => {
    await fetch(`${process.env.REACT_APP_API_HOST}/api/languages/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token?.access_token}` },
    });
    setConfirmDeleteId(null);
    await fetchLanguages();
  };

  return (
    <div>
      <Nav />
      <div className="max-w-xl mx-auto mt-8 text-white px-4">
        <h1 className="text-2xl font-bold mb-6">Manage Languages</h1>

        <form onSubmit={handleCreate} className="bg-gray-800 p-6 rounded-md shadow-md mb-8">
          <h2 className="text-lg font-semibold mb-4">Add New Language</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Language Code <span className="text-gray-500">(e.g. en, fr, de)</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              maxLength={10}
              className="p-2 w-full rounded-md bg-gray-700 text-white focus:outline-none focus:ring focus:border-blue-300"
              placeholder="en"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Language Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              className="p-2 w-full rounded-md bg-gray-700 text-white focus:outline-none focus:ring focus:border-blue-300"
              placeholder="English"
            />
          </div>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Add Language
          </button>
          {message && <p className="mt-3 text-sm text-yellow-300">{message}</p>}
        </form>

        <div className="bg-gray-800 p-6 rounded-md shadow-md">
          <h2 className="text-lg font-semibold mb-4">Existing Languages</h2>
          {languages.length === 0 ? (
            <p className="text-gray-400">No languages yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-600">
                  <th className="text-left py-2 pr-4">ID</th>
                  <th className="text-left py-2 pr-4">Code</th>
                  <th className="text-left py-2 pr-4">Name</th>
                  <th className="text-left py-2 pr-4">Users</th>
                  <th className="text-left py-2 pl-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {languages.map((lang) => (
                  <tr key={lang.id} className="border-b border-gray-700">
                    <td className="py-2 pr-4 text-gray-400">{lang.id}</td>
                    {editingId === lang.id ? (
                      <>
                        <td className="py-2 pr-2">
                          <input
                            value={editCode}
                            onChange={(e) => setEditCode(e.target.value)}
                            maxLength={10}
                            className="p-1 w-full rounded bg-gray-600 text-white text-sm"
                          />
                        </td>
                        <td className="py-2 pr-2">
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            maxLength={100}
                            className="p-1 w-full rounded bg-gray-600 text-white text-sm"
                          />
                        </td>
                        <td></td>
                        <td className="py-2 pl-4 flex gap-2">
                          <button
                            onClick={() => handleUpdate(lang.id)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1 rounded"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2 pr-4 font-mono">{lang.code}</td>
                        <td className="py-2 pr-4">{lang.name}</td>
                        <td className="py-2 pr-4 text-gray-400">{lang.user_count}</td>
                        <td className="py-2 pl-4">
                          {confirmDeleteId === lang.id ? (
                            <span className="flex flex-col gap-1">
                              <span className="text-yellow-300 text-xs">
                                Delete?{lang.user_count > 0 && ` ${lang.user_count} user(s) will be moved to English.`}
                              </span>
                              <span className="flex gap-2">
                              <button
                                onClick={() => handleDelete(lang.id)}
                                className="bg-red-700 hover:bg-red-800 text-white text-xs px-2 py-1 rounded"
                              >
                                Yes
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="bg-gray-500 hover:bg-gray-600 text-white text-xs px-2 py-1 rounded"
                              >
                                No
                              </button>
                              </span>
                            </span>
                          ) : (
                            <span className="flex gap-2">
                              <button
                                onClick={() => startEdit(lang)}
                                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-2 py-1 rounded"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(lang.id)}
                                className="bg-red-700 hover:bg-red-800 text-white text-xs px-2 py-1 rounded"
                              >
                                Delete
                              </button>
                            </span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
